<template>
  <div
    :class="[
      $style.mapContainer,
      {
        [$style.mainDataView]: viewMode === ViewMode.Data,
        [$style.mainMapView]: viewMode === ViewMode.Map,
        [$style.hg]: !isIs24,
      },
    ]"
    data-testid="map-wrapper"
  >
    <MapHeaderLayer v-if="!isIs24" />
    <MapLegend />
    <div
      v-if="isMapLoading"
      :class="$style.mapLoader"
      data-testId="map-loader"
      @wheel="handleScroll"
      @touchmove="handleTouchMove"
    >
      <HgSpinner />
    </div>
    <div v-else-if="mapLoadError" :class="$style.mapError" data-testid="map-load-error">
      <HgNotificationMessage
        :class="$style.mapErrorNotification"
        :theme="NotificationMessageTheme.Warning"
        :title="t('map.loadErrorTitle')"
      >
        {{ t('map.loadError') }}
      </HgNotificationMessage>
    </div>
    <MapboxWrapper data-testId="mapboxWrapper" @mapInteraction="handleMapInteraction" />
  </div>
</template>

<script setup lang="ts">
import { HgNotificationMessage, HgSpinner, NotificationMessageTheme } from '@smg-real-estate/domus';
import { storeToRefs } from 'pinia';
import { defineAsyncComponent } from 'vue';
import { useI18n } from 'vue-i18n';

import MapLegend from '@/components/map/mapbox/MapLegend.vue';
import MapHeaderLayer from '@/components/map/MapHeaderLayer.vue';
import { isPlatformIs24 } from '@/helpers/platform';
import useGeoStore from '@/stores/useGeoStore';
import useMapStore from '@/stores/useMapStore';
import { trackMapInteraction } from '@/tracking';
import { ViewMode } from '@/types';

const MapboxWrapper = defineAsyncComponent(
  () => import('@/components/map/mapbox/MapboxWrapper.vue'),
);

const { t } = useI18n();
const isIs24 = isPlatformIs24();
const mapStore = useMapStore();
const geoStore = useGeoStore();
const { isMapLoading, mapLoadError, viewMode } = storeToRefs(mapStore);
const { searchLocation } = storeToRefs(geoStore);

const handleMapInteraction = (): void => {
  if (searchLocation.value) {
    const { id, type } = searchLocation.value;
    trackMapInteraction(id, type);
  }
};
const handleScroll = (event: WheelEvent) => {
  event.preventDefault();
};
const handleTouchMove = (event: TouchEvent) => {
  if (event.cancelable) {
    event.preventDefault();
  }
};
</script>

<style lang="scss" module>
@use '@/styles/variables' as *;

.mapContainer {
  position: relative;

  &.hg {
    > div[class*='MapLegend_staticContainer'] {
      display: none;

      @include grid-media-query(sm) {
        display: block;
      }
    }
  }
}

.mainDataView {
  display: none;

  @include grid-media-query(sm) {
    display: block;
    width: 100%;
  }
}

.mainMapView {
  width: 100%;
  height: calc(100dvh - #{$header-height-sm});

  @include grid-media-query(sm) {
    height: auto;
  }
}

.mapLoader {
  position: absolute;
  z-index: 5;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: calc(100dvh - #{$header-height-sm});
  background: var(--is24-white, #fff);
  opacity: 0.6;

  @include grid-media-query(sm) {
    height: 100%;
  }
}

.mapError {
  position: absolute;
  z-index: 5;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: calc(100dvh - #{$header-height-sm});
  padding: $space-100;
  background: var(--is24-white, #fff);

  @include grid-media-query(sm) {
    height: 100%;
  }
}

.mapErrorNotification {
  width: 100%;
  max-width: 28rem;
}
</style>
